/**
 * 🟥 CUADRO NEGRO 2026-06-02 — SystemTemplatesPanel
 *
 * Modal que abre el botón "P de M" (Plantillas de Muestra) en el Área
 * Interna de Trabajo. Lista las plantillas oficiales del sistema y
 * permite duplicarlas con un click.
 *
 * Lógica:
 *  - Free  → ve solo plantillas FREE habilitadas. Las PREMIUM aparecen
 *            DESHABILITADAS (opacity 0.5). Click → toast "Disponible
 *            solo para Plan Premium".
 *  - Premium/Owner → ve todas habilitadas. Click → duplica y redirige.
 *
 * Same-origin: usa `/api/templates/...` sin REACT_APP_BACKEND_URL.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { X, Crown, Loader2, Copy, Pencil, Download, Trash2 } from 'lucide-react';

const fetchJSON = async (path, opts = {}) => {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
  if (!res.ok) {
    const err = new Error(data?.detail || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

const SystemTemplatesPanel = ({ open, onClose, language = 'es' }) => {
  const es = language === 'es';
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [isPremium, setIsPremium] = useState(false);
  const [loading, setLoading] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);
  // 🟪 P25.27 · Mostrar los trabajos generados (Mis Resultados) dentro
  // del mismo modal para que el usuario los encuentre sin salir de aquí.
  const [myRuns, setMyRuns] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchJSON('/api/templates/system');
      const visible = Array.isArray(data?.items) ? data.items : [];
      const userIsPremium = !!data?.is_premium;
      setIsPremium(userIsPremium);

      if (userIsPremium) {
        setItems(visible);
      } else {
        const premiumPlaceholders = [
          { id: 'sys-tpl-funnel-ia-automatico', name: es ? 'Funnel IA Automático' : 'Automatic AI Funnel',
            description: es ? 'Funnel completo con generación IA y secuencia automatizada.' : 'Full funnel with AI generation and automated sequence.',
            preview_color: '#EC4899', is_premium_only: true, is_system: true },
          { id: 'sys-tpl-landing-ia-ultra', name: es ? 'Landing IA Ultra-Optimizada' : 'Ultra-Optimized AI Landing',
            description: es ? 'Hero IA, beneficios dinámicos, testimonios IA y CTA optimizado.' : 'AI hero, dynamic benefits, AI testimonials and optimized CTA.',
            preview_color: '#EF4444', is_premium_only: true, is_system: true },
          { id: 'sys-tpl-dashboard-ia-predictivo', name: es ? 'Dashboard IA Predictivo' : 'Predictive AI Dashboard',
            description: es ? 'KPIs IA, insights automáticos y gráficos predictivos.' : 'AI KPIs, automatic insights and predictive charts.',
            preview_color: '#06B6D4', is_premium_only: true, is_system: true },
          { id: 'sys-tpl-doc-tecnica-ia', name: es ? 'Documentación Técnica IA' : 'AI Technical Documentation',
            description: es ? 'Resumen IA, arquitectura, endpoints y ejemplos autogenerados.' : 'AI summary, architecture, endpoints and autogenerated examples.',
            preview_color: '#22C55E', is_premium_only: true, is_system: true },
        ];
        setItems([...visible, ...premiumPlaceholders]);
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }

    // 🟪 P25.27 · Cargar los trabajos generados en paralelo (no bloquea el
    // render de las plantillas de sistema).
    try {
      const runs = await fetchJSON('/api/templates/my-runs');
      setMyRuns(Array.isArray(runs) ? runs : []);
    } catch {
      setMyRuns([]);
    }
  }, [es]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Cerrar con ESC.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handlePick = async (tpl) => {
    if (tpl.is_premium_only && !isPremium) {
      toast.info(es ? 'Disponible solo para Plan Premium' : 'Available only for Premium plan');
      return;
    }
    setDuplicatingId(tpl.id);
    try {
      const res = await fetchJSON(`/api/templates/${tpl.id}/duplicate`, { method: 'POST' });
      toast.success(es ? `Plantilla copiada: ${res.name || tpl.name}` : `Template copied: ${res.name || tpl.name}`);
      onClose?.();
      navigate(`/dashboard/workspace/editor/${res.workspace_item_id}`);
    } catch (e) {
      const detail = e?.data?.detail || e?.message || '';
      if (detail.includes('premium_required')) {
        toast.info(es ? 'Disponible solo para Plan Premium' : 'Available only for Premium plan');
      } else {
        toast.error(es ? `Error al copiar: ${detail}` : `Copy failed: ${detail}`);
      }
    } finally {
      setDuplicatingId(null);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="system-templates-panel"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0, 0, 0, 0.72)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0F1014',
          border: '1px solid #22232A',
          borderRadius: 16,
          width: '100%', maxWidth: 880,
          maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <header style={{
          padding: '14px 18px', borderBottom: '1px solid #22232A',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#FFFFFF' }}>
              {es ? 'Plantillas de Muestra' : 'Sample Templates'}
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#9A9CA1' }}>
              {es
                ? 'Selecciona una para crear una copia editable en tu Workspace.'
                : 'Pick one to create an editable copy in your Workspace.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="close-templates-panel"
            aria-label={es ? 'Cerrar' : 'Close'}
            style={{
              border: 'none', background: 'transparent', color: '#9A9CA1',
              cursor: 'pointer', padding: 6, borderRadius: 8,
            }}
          >
            <X style={{ width: 18, height: 18 }} />
          </button>
        </header>

        <div style={{ overflowY: 'auto', padding: 16 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 36 }}>
              <Loader2 style={{ width: 24, height: 24, color: '#6C47FF' }} className="animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <p style={{ color: '#9A9CA1', textAlign: 'center', padding: 24 }}>
              {es ? 'No hay plantillas disponibles.' : 'No templates available.'}
            </p>
          ) : (
            <div
              data-testid="system-templates-panel-grid"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 14,
              }}
            >
              {items.map((tpl) => {
                const disabled = tpl.is_premium_only && !isPremium;
                return (
                  <article
                    key={tpl.id}
                    data-testid={`pdm-card-${tpl.id}`}
                    data-disabled={disabled ? '1' : '0'}
                    style={{
                      position: 'relative',
                      background: '#15161A',
                      border: '1px solid #22232A',
                      borderRadius: 12,
                      padding: 12,
                      display: 'flex', flexDirection: 'column', gap: 10,
                      opacity: disabled ? 0.5 : 1,
                      transition: 'box-shadow 0.18s, transform 0.18s',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      if (!disabled) {
                        e.currentTarget.style.boxShadow = '0 8px 24px rgba(108, 71, 255, 0.18)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'none';
                    }}
                    onClick={() => duplicatingId !== tpl.id && handlePick(tpl)}
                  >
                    <div
                      style={{
                        height: 56,
                        borderRadius: 8,
                        background: `linear-gradient(135deg, ${tpl.preview_color || '#6366f1'} 0%, #0F1014 110%)`,
                        position: 'relative',
                      }}
                    >
                      {tpl.is_premium_only && (
                        <span
                          data-testid="pdm-premium-badge"
                          style={{
                            position: 'absolute', top: 6, right: 6,
                            background: 'rgba(245, 158, 11, 0.92)',
                            color: '#1a1206', fontSize: 10, fontWeight: 700,
                            padding: '3px 7px', borderRadius: 999,
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            letterSpacing: 0.3,
                          }}
                        >
                          <Crown style={{ width: 11, height: 11 }} /> PREMIUM
                        </span>
                      )}
                    </div>
                    <div>
                      <h3 style={{ margin: 0, color: '#fff', fontSize: 13.5, fontWeight: 600 }}>
                        {tpl.name}
                      </h3>
                      <p style={{
                        margin: '4px 0 0', color: '#9A9CA1', fontSize: 11.5, lineHeight: 1.35,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        overflow: 'hidden', minHeight: 32,
                      }}>
                        {tpl.description}
                      </p>
                    </div>
                    <button
                      type="button"
                      data-testid={`pdm-use-btn-${tpl.id}`}
                      disabled={duplicatingId === tpl.id}
                      onClick={(e) => { e.stopPropagation(); handlePick(tpl); }}
                      style={{
                        marginTop: 'auto',
                        background: disabled ? '#3a2a08' : '#6C47FF',
                        color: disabled ? '#fbbf24' : '#fff',
                        border: 'none', borderRadius: 8,
                        padding: '8px 10px', fontSize: 12, fontWeight: 600,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      }}
                      title={disabled
                        ? (es ? 'Solo para Plan Premium' : 'Premium only')
                        : (es ? 'Usar esta plantilla' : 'Use this template')}
                    >
                      {duplicatingId === tpl.id ? (
                        <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" />
                      ) : disabled ? (
                        <><Crown style={{ width: 13, height: 13 }} />{es ? 'Solo Premium' : 'Premium only'}</>
                      ) : (
                        <><Copy style={{ width: 13, height: 13 }} />{es ? 'Enviar al Workspace' : 'Send to Workspace'}</>
                      )}
                    </button>
                  </article>
                );
              })}
            </div>
          )}

          {/* 🟪 P25.27 · Mis Resultados generados por IA — se muestra dentro
              del mismo modal para que el usuario encuentre sus trabajos
              inmediatamente sin cerrar el panel. */}
          {myRuns.length > 0 && (
            <section
              data-testid="pdm-my-results-section"
              style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #22232A' }}
            >
              <header style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <h3 style={{ margin: 0, color: '#fff', fontSize: 14, fontWeight: 600 }}>
                  {es ? 'Mis Resultados' : 'My Results'} ({myRuns.length})
                </h3>
                <span style={{ fontSize: 11, color: '#6B6D75', marginLeft: 'auto' }}>
                  {es ? 'Trabajos generados por IA' : 'AI-generated works'}
                </span>
              </header>
              <div
                data-testid="pdm-my-results-list"
                style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
              >
                {myRuns.map((run) => (
                  <div
                    key={run.id}
                    data-testid={`pdm-my-result-card-${run.id}`}
                    style={{
                      background: '#15161A',
                      border: '1px solid #22232A',
                      borderRadius: 10,
                      padding: '12px 14px',
                      color: '#E5E7EB',
                      display: 'flex', gap: 12, alignItems: 'flex-start',
                    }}
                  >
                    <button
                      type="button"
                      data-testid={`pdm-my-result-open-${run.id}`}
                      onClick={() => { onClose?.(); navigate(`/dashboard/workspace/editor/${run.id}`); }}
                      style={{
                        background: 'transparent', border: 'none', padding: 0, margin: 0,
                        color: 'inherit', textAlign: 'left', cursor: 'pointer',
                        display: 'flex', gap: 12, alignItems: 'flex-start', flex: 1, minWidth: 0,
                      }}
                    >
                      <div style={{
                        width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                        background: `linear-gradient(135deg, ${run.preview_color || '#6C47FF'} 0%, #0F1014 110%)`,
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                          gap: 8, marginBottom: 3,
                        }}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#FFFFFF' }}>{run.name}</span>
                          <span style={{ fontSize: 10.5, color: '#6B6D75', flexShrink: 0 }}>
                            {run.last_run_at
                              ? new Date(run.last_run_at).toLocaleString(es ? 'es-ES' : 'en-US', {
                                  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                                })
                              : ''}
                          </span>
                        </div>
                        <p style={{
                          margin: 0, color: '#9A9CA1', fontSize: 11.5, lineHeight: 1.4,
                          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>{run.preview}</p>
                        <span style={{
                          display: 'inline-block', marginTop: 6,
                          fontSize: 10.5, color: '#A78BFA', fontWeight: 500,
                        }}>
                          {es ? `Abrir · ${run.output_length.toLocaleString()} chars` : `Open · ${run.output_length.toLocaleString()} chars`}
                        </span>
                      </div>
                    </button>
                    {/* 🟪 P25.28 · Acciones por tarjeta: Editar / Exportar / Borrar */}
                    <div
                      style={{
                        display: 'flex', flexDirection: 'column', gap: 6,
                        flexShrink: 0,
                      }}
                    >
                      <button
                        type="button"
                        title={es ? 'Editar plantilla' : 'Edit template'}
                        data-testid={`pdm-my-result-edit-${run.id}`}
                        onClick={() => { onClose?.(); navigate(`/dashboard/workspace/editor/${run.id}?edit=1`); }}
                        style={{
                          width: 30, height: 30, borderRadius: 7,
                          background: '#1F2029', border: '1px solid #2C2E38',
                          color: '#A78BFA', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <Pencil style={{ width: 13, height: 13 }} />
                      </button>
                      <button
                        type="button"
                        title={es ? 'Exportar como .md' : 'Export as .md'}
                        data-testid={`pdm-my-result-export-${run.id}`}
                        onClick={async () => {
                          try {
                            const res = await fetch(`/api/templates/${run.id}`, { credentials: 'include' });
                            const full = res.ok ? await res.json() : null;
                            const output = full?.last_run_output || '';
                            if (!output) { toast.error(es ? 'Sin contenido' : 'No content'); return; }
                            const blob = new Blob([output], { type: 'text/markdown;charset=utf-8' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            const safe = (run.name || 'resultado').replace(/[^a-z0-9-_]+/gi, '_').slice(0, 60);
                            a.href = url; a.download = `${safe}.md`;
                            document.body.appendChild(a); a.click();
                            setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
                            toast.success(es ? 'Descargado' : 'Downloaded');
                          } catch { toast.error(es ? 'Error al exportar' : 'Export failed'); }
                        }}
                        style={{
                          width: 30, height: 30, borderRadius: 7,
                          background: '#1F2029', border: '1px solid #2C2E38',
                          color: '#38BDF8', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <Download style={{ width: 13, height: 13 }} />
                      </button>
                      <button
                        type="button"
                        title={es ? 'Borrar resultado' : 'Delete result'}
                        data-testid={`pdm-my-result-delete-${run.id}`}
                        onClick={async () => {
                          const ok = window.confirm(
                            es
                              ? `¿Borrar el resultado de "${run.name}"? La plantilla no se elimina.`
                              : `Delete the result of "${run.name}"? The template stays.`
                          );
                          if (!ok) return;
                          try {
                            const res = await fetch(`/api/templates/${run.id}/run`, {
                              method: 'DELETE', credentials: 'include',
                            });
                            if (!res.ok) throw new Error(String(res.status));
                            setMyRuns((prev) => prev.filter((r) => r.id !== run.id));
                            toast.success(es ? 'Resultado borrado' : 'Result deleted');
                          } catch {
                            toast.error(es ? 'Error al borrar' : 'Delete failed');
                          }
                        }}
                        style={{
                          width: 30, height: 30, borderRadius: 7,
                          background: '#1F2029', border: '1px solid #2C2E38',
                          color: '#F87171', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        <Trash2 style={{ width: 13, height: 13 }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemTemplatesPanel;
